import type React from 'react';
import {useMemo, useState} from 'react';
import {MenuItem, Stack, TextField, Typography} from '@mui/material';
import {impactAtLeast} from '../../domain/signals';
import type {ExternalSignals} from '../../types/signalsTypes';
import {ProfileSection} from '../components/ProfileSection';
import {SignalCard} from './SignalCard';

const ALL = 'all';

/** The signals feed (spec 4.12) with filters: impact type, event category, language and search. */
export const SignalsFeed: React.FC<{data: ExternalSignals}> = ({data}) => {
    const [type, setType] = useState(ALL);
    const [event, setEvent] = useState(ALL);
    const [language, setLanguage] = useState(ALL);
    const [search, setSearch] = useState('');
    const events = useMemo(() => [...new Set(data.signals.map(s => s.eventCategory).filter(Boolean))].sort(), [data.signals]);
    const languages = useMemo(() => [...new Set(data.signals.map(s => s.language))].sort(), [data.signals]);
    const q = search.trim().toLowerCase();
    const shown = data.signals.filter(s => (type === ALL || s.impactType === type) && (event === ALL || s.eventCategory === event)
        && (language === ALL || s.language === language)
        && (!q || `${s.title} ${s.summary} ${s.publisher}`.toLowerCase().includes(q)));
    const alerts = data.signals.filter(s => impactAtLeast(s.impact, data.alertImpact)).length;

    const select = (label: string, value: string, set: (v: string) => void, options: {value: string; label: string}[]) => (
        <TextField select size="small" label={label} value={value} onChange={e => set(e.target.value)} sx={{minWidth: 160}}>
            <MenuItem value={ALL}>All</MenuItem>
            {options.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
    );

    return (
        <ProfileSection title="External signals" subtitle={`${data.signals.length} signals · ${alerts} alert${alerts === 1 ? '' : 's'}`}>
            {data.signals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                    {data.provider.enabled ? 'No signals for this category yet.'
                        : 'No signals yet. The provider feed is not switched on for this workspace; Mithra can add signals by hand meanwhile.'}
                </Typography>
            ) : (
                <Stack gap={1.5}>
                    <Stack direction="row" gap={1.5} flexWrap="wrap">
                        {select('Impact type', type, setType, data.options.impactTypes)}
                        {select('Event', event, setEvent, events.map(e => ({value: e, label: e})))}
                        {select('Language', language, setLanguage, languages.map(l => ({value: l, label: l.toUpperCase()})))}
                        <TextField size="small" label="Search" value={search} onChange={e => setSearch(e.target.value)} sx={{minWidth: 220}}/>
                    </Stack>
                    {shown.length === 0 && <Typography variant="body2" color="text.secondary">No signals match the filters.</Typography>}
                    {shown.map(s => <SignalCard key={s.id} signal={s} options={data.options} alert={impactAtLeast(s.impact, data.alertImpact)}/>)}
                </Stack>
            )}
        </ProfileSection>
    );
};
